/**
 * Pure status logic for the Review Teachers screen (Teacher Allocation > Add Teachers).
 *
 * Blocking statuses keep "Confirm Teachers & Continue" disabled; `verify` is advisory only
 * (an extracted row the user should eyeball) and never blocks.
 */

export type TeacherReviewStatus =
  | 'ok'
  /** Empty name. */
  | 'name-required'
  /** Two or more teachers with exactly the same name and no unique Employee ID each. */
  | 'same-name'
  /** The same Employee ID on two different teachers. */
  | 'duplicate-id'
  /** Looks like another teacher in the list (`similarTo`): keep both or remove one. */
  | 'similar'
  /** Low-confidence extraction — please check the spelling. */
  | 'verify'

export const BLOCKING_TEACHER_STATUSES: ReadonlySet<TeacherReviewStatus> = new Set([
  'name-required',
  'same-name',
  'duplicate-id',
])

interface ReviewableTeacher {
  first_name?: string
  last_name?: string
  _nameInput?: string
  employeeId?: string
  _needsReview?: boolean
  allocationHints?: unknown[]
  _similarTo?: string[]
}

/**
 * The name as the user currently sees it (draft input wins over the committed split). The
 * draft is returned VERBATIM: it feeds a controlled input, and trimming it here swallowed the
 * space the user had just typed between first and last name. Comparisons trim on their side.
 */
export const teacherDisplayName = (t: ReviewableTeacher): string =>
  t._nameInput !== undefined ? t._nameInput : `${t.first_name || ''} ${t.last_name || ''}`.trim()

/**
 * The teachers still in the list that row `index` may be the same person as (its `_similarTo`,
 * matched by name, case-insensitive), as they are named in the list. Empty once the user kept
 * both, renamed one, or removed the other.
 */
export const similarTeachersOf = (rows: readonly ReviewableTeacher[], index: number): string[] => {
  const wanted = new Set(
    (rows[index]?._similarTo ?? []).map((name) => name.trim().toLowerCase()).filter(Boolean)
  )
  if (wanted.size === 0) return []
  const found: string[] = []
  rows.forEach((row, i) => {
    if (i === index) return
    const name = teacherDisplayName(row).trim()
    if (name && wanted.has(name.toLowerCase()) && !found.includes(name)) found.push(name)
  })
  return found
}

export interface TeacherReviewSummary {
  statuses: TeacherReviewStatus[]
  /** Rows with a blocking status. */
  attentionCount: number
  /**
   * Rows to look at that do not block Continue ("Possibly a duplicate", "Please verify"): the
   * "To check" card, so the header counts what the rows show (QA 13 Oct, #369).
   */
  checkCount: number
  /** Teachers that carry at least one allocation hint. */
  teachersWithAllocation: number
  /** Total subject/class/section hints across all teachers. */
  allocationRecords: number
  canContinue: boolean
}

/**
 * Status per row + the headline numbers. Same-name detection is case-insensitive; a
 * same-name group only clears when every member has a distinct, non-empty Employee ID.
 * Nothing is auto-resolved — no generated IDs, no renaming.
 */
export const summarizeTeacherReview = (
  rows: readonly ReviewableTeacher[]
): TeacherReviewSummary => {
  const names = rows.map((r) => teacherDisplayName(r).trim())
  const ids = rows.map((r) => String(r.employeeId ?? '').trim())

  const byName = new Map<string, number[]>()
  names.forEach((n, i) => {
    const key = n.toLowerCase()
    if (!key) return
    byName.set(key, [...(byName.get(key) ?? []), i])
  })
  const byId = new Map<string, number[]>()
  ids.forEach((id, i) => {
    if (!id) return
    const key = id.toLowerCase()
    byId.set(key, [...(byId.get(key) ?? []), i])
  })

  const statuses: TeacherReviewStatus[] = rows.map((row, i) => {
    if (!names[i]) return 'name-required'
    const group = byName.get(names[i].toLowerCase()) ?? []
    if (group.length > 1) {
      const groupIds = group.map((g) => ids[g].toLowerCase())
      if (groupIds.some((id) => !id) || new Set(groupIds).size !== groupIds.length) {
        return 'same-name'
      }
    }
    if (ids[i] && (byId.get(ids[i].toLowerCase()) ?? []).length > 1) return 'duplicate-id'
    // Advisory, like `verify`: two different teachers may well have look-alike names.
    if (similarTeachersOf(rows, i).length > 0) return 'similar'
    if (row._needsReview) return 'verify'
    return 'ok'
  })

  const attentionCount = statuses.filter((s) => BLOCKING_TEACHER_STATUSES.has(s)).length
  const checkCount = statuses.filter((s) => s === 'similar' || s === 'verify').length
  let teachersWithAllocation = 0
  let allocationRecords = 0
  rows.forEach((r) => {
    const n = Array.isArray(r.allocationHints) ? r.allocationHints.length : 0
    if (n > 0) {
      teachersWithAllocation += 1
      allocationRecords += n
    }
  })

  return {
    statuses,
    attentionCount,
    checkCount,
    teachersWithAllocation,
    allocationRecords,
    canContinue: rows.length > 0 && attentionCount === 0,
  }
}

/** Case-insensitive match on name or Employee ID for the review search box. */
export const teacherMatchesQuery = (t: ReviewableTeacher, query: string): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    teacherDisplayName(t).trim().toLowerCase().includes(q) ||
    String(t.employeeId ?? '')
      .toLowerCase()
      .includes(q)
  )
}

/** The "Found in 2 files" / "Found twice" badge of one Review row: a message id and its values. */
export interface TeacherFoundBadge {
  id: string
  values: {count: number; files: number}
}

const FOUND_KEY = 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.REVIEW'

/**
 * The badge under a teacher's name when repeated entries of the upload were combined into them
 * (`_foundTimes` > 1), else null (no badge, no tooltip). First match wins:
 * several files with more entries than files → "Found {n} times in {m} files"; several files →
 * "Found in {m} files"; otherwise "Found twice" / "Found {n} times" (one plural message).
 */
export const teacherFoundBadge = (row: {
  _foundTimes?: unknown
  _fileCount?: unknown
}): TeacherFoundBadge | null => {
  const count = Number(row?._foundTimes)
  if (!Number.isInteger(count) || count <= 1) return null
  const fileCount = Number(row?._fileCount)
  const files = Number.isInteger(fileCount) && fileCount >= 1 ? Math.min(fileCount, count) : 1
  const values = {count, files}
  if (files > 1 && count > files) return {id: `${FOUND_KEY}.FOUND_TIMES_IN_FILES`, values}
  if (files > 1) return {id: `${FOUND_KEY}.FOUND_IN_FILES`, values}
  return {id: `${FOUND_KEY}.FOUND_TIMES`, values}
}

/** The badge's tooltip: one sentence, whichever badge shows. */
export const TEACHER_FOUND_HINT_KEY = `${FOUND_KEY}.FOUND_COMBINED_HINT`
